/**
 * A message's lifetime (docs/disappearing-messages.md D296–D298): the wire's
 * range, mirrored, and the three ways this client says a span of time — a
 * lifetime in words, a lifetime as a label, and the time one has left. The
 * words themselves are `lib/i18n.ts`'s, where every other word is; which unit
 * they are said in is decided here, once, for both languages.
 */
import type { Message } from "../gen/dto";

import type { Dict } from "./i18n";

/** `echo_wire::account::MIN_MESSAGE_TTL_S` and `MAX_MESSAGE_TTL_S`: the range
 * `ttl_s` is held to, both ends included. Outside it the gateway answers 400
 * rather than clamping (D296) — the one promise here is about when the words
 * are gone — so nothing out of range may leave this client either. */
export const MIN_MESSAGE_TTL_S = 5;
export const MAX_MESSAGE_TTL_S = 86_400;
/** `MESSAGE_TTL_PRESETS_S`: the lifetimes the composer's clock lists, shortest
 * first (D298). A custom one may be anything in the range. */
export const MESSAGE_TTL_PRESETS_S: readonly number[] = [5, 10, 30, 60, 300, 3600, 86_400];

/** `is_valid_message_ttl`, and whole seconds only: `ttl_s` is a `u32`. */
export function isValidTtl(ttlS: number): boolean {
  return Number.isInteger(ttlS) && ttlS >= MIN_MESSAGE_TTL_S && ttlS <= MAX_MESSAGE_TTL_S;
}

/** The lifetime a message's sender gave it, in seconds (`Message.ttl_s`), or
 * null for one that stays. The JSON boundary is not shape-checked, so anything
 * that is not a positive number of seconds reads as «stays» — never as a
 * lifetime of zero, which would be a message drawn as already gone. */
export function ttlOf(m: Pick<Message, "ttl_s">): number | null {
  const ttl = m.ttl_s;
  return typeof ttl === "number" && Number.isFinite(ttl) && ttl > 0 ? Math.round(ttl) : null;
}

/** The lifetime the message is waiting to start counting (D302): it has one,
 * and no deadline yet, because nobody but its author has read it. Null on a
 * message that stays and on one already counting. `Message::ttl_waiting` on
 * the Rust side. */
export function ttlWaiting(m: Pick<Message, "ttl_s" | "expires_at_ms">): number | null {
  return Number.isFinite(m.expires_at_ms as number) ? null : ttlOf(m);
}

/** The unit a lifetime is said in. */
export type TtlUnit = "s" | "m" | "h" | "d";

const UNIT_S: Record<TtlUnit, number> = { s: 1, m: 60, h: 3600, d: 86_400 };

/** The largest unit that divides the lifetime evenly: 86400 is «1 день» and
 * not «24 часа», 300 is «5 минут», and 90 stays «90 секунд» — a lifetime is
 * never rounded to be said, since what it says is when the words are gone. */
export function ttlUnit(ttlS: number): { n: number; unit: TtlUnit } {
  for (const unit of ["d", "h", "m"] as const) {
    if (ttlS >= UNIT_S[unit] && ttlS % UNIT_S[unit] === 0) return { n: ttlS / UNIT_S[unit], unit };
  }
  return { n: ttlS, unit: "s" };
}

/** In words — a row of the clock's menu: «30 секунд», «1 минута», «1 час»,
 * "5 minutes". */
export function ttlLong(ttlS: number, t: Dict): string {
  const { n, unit } = ttlUnit(ttlS);
  return t.ttlLong(n, unit);
}

/** The same words inside a sentence, after «через» — the clock's tooltip, the
 * box's placeholder: «через 1 минуту». English reads `ttlLong` as it is. */
export function ttlAfter(ttlS: number, t: Dict): string {
  const { n, unit } = ttlUnit(ttlS);
  return t.ttlAfter(n, unit);
}

/** As a label — on the composer's clock, and on a bubble that is still on its
 * way (D298): «30 с», «1 ч», "5m". */
export function ttlShort(ttlS: number, t: Dict): string {
  const { n, unit } = ttlUnit(ttlS);
  return t.ttlShort(n, unit);
}

/** What a countdown shows, from the milliseconds left: `m:ss` under an hour,
 * whole hours from there up — rounded up both ways, so a message sent for five
 * seconds starts at `0:05`, reads `0:01` through its last second and «1 ч»
 * through its last full hour. Never negative: a deadline already past reads
 * `0:00` for the moment it takes the runtime to let the message go. */
export function timeLeft(leftMs: number, t: Dict): string {
  const s = Math.ceil(Math.max(0, leftMs) / 1000);
  if (s >= 3600) return t.ttlHoursLeft(Math.ceil(s / 3600));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
